import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiConfig } from '~shared/constants/api.config';
import {
  CheckoutTrackingStepOptionTypes,
  PaymentMethods,
  PaymentMethodState,
  PaymentRequest,
  ServicePaymentMethodTypes
} from '../models';
import { CheckoutStateSharedKeys, CheckoutStateSharedService } from './checkout-state-shared.service';
import { AdditionalInformation } from '../models/payment-additional-info.model';
import * as Sentry from '@sentry/browser';
import { PaymentMethodOptions } from '../constants';

@Injectable()
export class PaymentMethodsService {
  private isPaymentDetailsSaved = new BehaviorSubject<boolean>(false);
  private paymentDetails = new BehaviorSubject<PaymentMethodState>(null);
  private static readonly usedPaymentMethodMap: Record<ServicePaymentMethodTypes, CheckoutTrackingStepOptionTypes> = {
    [ServicePaymentMethodTypes.CreditCard]: CheckoutTrackingStepOptionTypes.CreditCard,
    [ServicePaymentMethodTypes.RatePayInvoice]: CheckoutTrackingStepOptionTypes.RatePay,
    [ServicePaymentMethodTypes.RatePayDirectDebit]: CheckoutTrackingStepOptionTypes.DirectDebit,
    [ServicePaymentMethodTypes.Paypal]: CheckoutTrackingStepOptionTypes.Paypal,
    [ServicePaymentMethodTypes.DirectEbanking]: CheckoutTrackingStepOptionTypes.DirectEbanking,
    [ServicePaymentMethodTypes.IDeal]: CheckoutTrackingStepOptionTypes.IDeal,
    [ServicePaymentMethodTypes.PayPal_ins_FR]: CheckoutTrackingStepOptionTypes.PayPal_ins_FR,
    [ServicePaymentMethodTypes.PayPal_ins_ES]: CheckoutTrackingStepOptionTypes.PayPal_ins_ES,
    [ServicePaymentMethodTypes.PayPal_ins_IT]: CheckoutTrackingStepOptionTypes.PayPal_ins_IT,
    [ServicePaymentMethodTypes.SepaDebitDirect]: CheckoutTrackingStepOptionTypes.SepaDebitDirect,
    [ServicePaymentMethodTypes.Billie]: CheckoutTrackingStepOptionTypes.Billie,
    [ServicePaymentMethodTypes.MetroInvoice]: CheckoutTrackingStepOptionTypes.MetroInvoice,
    [ServicePaymentMethodTypes.PayByBank]: CheckoutTrackingStepOptionTypes.PayByBank,
  }
  private static readonly usedPaymentMethodResponseMap: Record<Exclude<PaymentMethodOptions, ''>, CheckoutTrackingStepOptionTypes> = {
    [PaymentMethodOptions.CreditCard]: CheckoutTrackingStepOptionTypes.CreditCard,
    [PaymentMethodOptions.RatePayInvoice]: CheckoutTrackingStepOptionTypes.RatePay,
    [PaymentMethodOptions.RatePayDirectDebit]: CheckoutTrackingStepOptionTypes.DirectDebit,
    [PaymentMethodOptions.Paypal]: CheckoutTrackingStepOptionTypes.Paypal,
    [PaymentMethodOptions.DirectEbanking]: CheckoutTrackingStepOptionTypes.DirectEbanking,
    [PaymentMethodOptions.IDeal]: CheckoutTrackingStepOptionTypes.IDeal,
    [PaymentMethodOptions.PayPal_ins_FR]: CheckoutTrackingStepOptionTypes.PayPal_ins_FR,
    [PaymentMethodOptions.PayPal_ins_ES]: CheckoutTrackingStepOptionTypes.PayPal_ins_ES,
    [PaymentMethodOptions.PayPal_ins_IT]: CheckoutTrackingStepOptionTypes.PayPal_ins_IT,
    [PaymentMethodOptions.SepaDebitDirect]: CheckoutTrackingStepOptionTypes.SepaDebitDirect,
    [PaymentMethodOptions.Billie]: CheckoutTrackingStepOptionTypes.Billie,
    [PaymentMethodOptions.MetroInvoice]: CheckoutTrackingStepOptionTypes.MetroInvoice,
    [PaymentMethodOptions.PayByBank]: CheckoutTrackingStepOptionTypes.PayByBank,
  }
  isPaymentDetailsSaved$ = this.isPaymentDetailsSaved.asObservable();
  paymentDetails$ = this.paymentDetails.asObservable();

  constructor(private httpClient: HttpClient) {}

  fetchPaymentMethods(country: string): Observable<PaymentMethods[]> {
    return this.httpClient.get<PaymentMethods[]>(`${ApiConfig.SVC_API_URL()}/payment/get-available-methods/${country}`);
  }

  updatePaymentDetails(payload: PaymentMethodState): void {
    this.isPaymentDetailsSaved.next(false);
    this.paymentDetails.next(payload);
  }

  rehydratePaymentDetailsFromRequest(payload: PaymentRequest): void {
    this.paymentDetails.next({
      deviceFingerprint: payload.deviceFingerprint,
      buyerPhone: payload.buyerPhone,
      ...(payload.bankAccountDetails ? { bankAccountDetails: payload.bankAccountDetails } : {}),
      ...(payload.additionalInformation ? { additionalInformation: payload.additionalInformation } : {}),
      additionalInformation: null,
      isValid: true,
      data: {
        browserInfo: payload.browserInfo,
        paymentMethod: payload.paymentMethod,
      },
    });
    this.isPaymentDetailsSaved.next(true);
  }

  enablePaymentDetailsEdit(): void {
    this.isPaymentDetailsSaved.next(false);
  }

  savePaymentDetails(): void {
    this.isPaymentDetailsSaved.next(true);
  }

  resetPaymentDetails(): void {
    this.isPaymentDetailsSaved.next(false);
    this.paymentDetails.next(null);
  }

  getUsedPaymentMethod(paymentMethod): CheckoutTrackingStepOptionTypes {
    return PaymentMethodsService.usedPaymentMethodMap[paymentMethod]
  }

  static cachePaymentMethodStateCC(state: PaymentMethodState): void{
    CheckoutStateSharedService.set(CheckoutStateSharedKeys.PaymentMethodStateCC, state);
  }

  static get cachedPaymentMethodStateCC(): PaymentMethodState{
    return CheckoutStateSharedService.get<PaymentMethodState>(CheckoutStateSharedKeys.PaymentMethodStateCC);
  }

  appendToAdditionalInfo(
    state: PaymentRequest,
    additionalInfoData: Partial<Record<keyof AdditionalInformation, any>>
  ): void {
    try{
      state.additionalInformation = {
        ...(state.additionalInformation || {}),
        ...additionalInfoData,
      };
    }catch(e){
      Sentry.captureException(new Error("Checkout - appendToAdditionalInfo - failed to append in additionalInfo"));
    }
  }

  static getPaymentCheckoutTrackingName(paymentMethod: string) : string {
    return PaymentMethodsService.usedPaymentMethodMap[paymentMethod] ?? PaymentMethodsService.usedPaymentMethodResponseMap[paymentMethod]
  }
}
